import type {Object3D} from 'three';
import {ownsObject3DGeometry} from '../../../utils/scene-object/ownsObject3DGeometry';
import {addObject3DToParent} from '../../../utils/scene-object/addObject3DToParent';
import type {
	DisposableGeometry,
	DisposableSceneObject,
	SketchEntityReferenceTargetRetirement,
} from '../../../../types/reference/sketch-entity-reference/SketchEntityReferenceObjectCleanupService';

/**
 * 草图实体引用对象清理服务。
 * 说明：
 * 1. Convert Entities 的 preview / create / rollback 都会生成临时或新建草图对象；
 * 2. 这里统一执行“场景树摘除 + 自有 geometry 释放”，避免不同入口重复写清理细节；
 * 3. 默认只释放 geometry，不触碰共享材质，避免误伤全局缓存资源。
 */
export class SketchEntityReferenceObjectCleanupService {
	/**
	 * 开始目标实体退役，但把资源释放延迟到正式提交之后。
	 *
	 * @param targetEntity 待退役目标实体。
	 * @returns 可提交或回滚的退役句柄。
	 */
	public beginTargetRetirement(
		targetEntity: Object3D,
	): SketchEntityReferenceTargetRetirement {
		const parent = targetEntity.parent;
		const childIndex = parent?.children.indexOf(targetEntity) ?? -1;
		let state: 'open' | 'committed' | 'rolled-back' = 'open';
		parent?.remove(targetEntity);

		return {
			commit: () => {
				if (state !== 'open') {
					return;
				}
				state = 'committed';
				this.cleanupTargetEntity(targetEntity);
			},
			rollback: () => {
				if (state !== 'open') {
					return;
				}
				state = 'rolled-back';
				if (parent) {
					addObject3DToParent(
						targetEntity,
						parent,
						childIndex >= 0 ? childIndex : undefined,
					);
				}
			},
		};
	}

	/**
	 * 清理一组目标实体及其关联点。
	 *
	 * @param targetEntities 需要清理的目标实体集合。
	 */
	public cleanupTargetEntities(targetEntities: Object3D[]): void {
		const cleanupTargets = this.collectCleanupTargets(targetEntities);
		cleanupTargets.forEach((cleanupTarget) => {
			cleanupTarget.parent?.remove(cleanupTarget);
		});
		this.disposeGeometryResources(cleanupTargets);
	}

	/**
	 * 清理单个目标实体及其关联点。
	 *
	 * @param targetEntity 需要清理的目标实体。
	 */
	public cleanupTargetEntity(targetEntity: Object3D): void {
		this.cleanupTargetEntities([targetEntity]);
	}

	/**
	 * 收集与目标实体一起需要清理的对象。
	 * 说明：
	 * 1. line / ellipse 的控制点可能直接挂在 target sketch，而不是主实体子节点；
	 * 2. 因此不能只删除主实体，必须把关联点一并带走；
	 * 3. 这里保持弱约定读取，不把清理层反向耦合到具体草图对象类。
	 *
	 * @param targetEntities 目标实体集合。
	 * @returns 去重后的待清理对象集合。
	 */
	private collectCleanupTargets(targetEntities: Object3D[]): Object3D[] {
		const cleanupTargets = new Set<Object3D>();
		const targetEntitySet = new Set(targetEntities);

		targetEntities.forEach((targetEntity) => {
			const maybeTarget = targetEntity as Object3D & {
				startPoint?: Object3D;
				endPoint?: Object3D;
				getRelatedSketchPointsSnapshot?: () => readonly Object3D[];
			};

			const relatedPoints = new Set<Object3D>();
			if (maybeTarget.startPoint) relatedPoints.add(maybeTarget.startPoint);
			if (maybeTarget.endPoint) relatedPoints.add(maybeTarget.endPoint);

			if (typeof maybeTarget.getRelatedSketchPointsSnapshot === 'function') {
				maybeTarget.getRelatedSketchPointsSnapshot().forEach((pointObject) => {
					relatedPoints.add(pointObject);
				});
			}

			relatedPoints.forEach((pointObject) => {
				this.detachTargetEntityFromPoint(targetEntity, pointObject);
				if (this.hasSurvivingConnectedEdge(pointObject, targetEntitySet)) {
					if (pointObject.parent === targetEntity && targetEntity.parent) {
						addObject3DToParent(pointObject, targetEntity.parent);
					}
					return;
				}
				cleanupTargets.add(pointObject);
			});

			cleanupTargets.add(targetEntity);
		});

		return [...cleanupTargets];
	}

	/**
	 * 解除待清理实体在关联点上的拓扑登记。
	 *
	 * @param targetEntity 待清理实体。
	 * @param pointObject 实体关联点。
	 */
	private detachTargetEntityFromPoint(targetEntity: Object3D, pointObject: Object3D): void {
		const isEndpoint = this.isTargetEntityEndpoint(targetEntity, pointObject);
		const unregisterConnectedEdge = Reflect.get(pointObject, 'unregisterConnectedEdge');
		if (typeof unregisterConnectedEdge === 'function') {
			unregisterConnectedEdge.call(pointObject, targetEntity);
		}
		if (isEndpoint) {
			const detachEndpointRole = Reflect.get(pointObject, 'detachEndpointRole');
			if (typeof detachEndpointRole === 'function') {
				detachEndpointRole.call(pointObject);
			}
		}
		const unregisterRelatedSketchObject = Reflect.get(pointObject, 'unregisterRelatedSketchObject');
		if (typeof unregisterRelatedSketchObject === 'function') {
			unregisterRelatedSketchObject.call(pointObject, targetEntity);
		}
	}

	/**
	 * 判断点是否承担目标实体的首尾端点角色。
	 *
	 * @param targetEntity 目标实体。
	 * @param pointObject 待判断点。
	 * @returns 点是首端点或尾端点时返回 true。
	 */
	private isTargetEntityEndpoint(targetEntity: Object3D, pointObject: Object3D): boolean {
		if (
			Reflect.get(targetEntity, 'startPoint') === pointObject
			|| Reflect.get(targetEntity, 'endPoint') === pointObject
		) {
			return true;
		}
		const getStartPoint = Reflect.get(targetEntity, 'getStartPoint');
		const getEndPoint = Reflect.get(targetEntity, 'getEndPoint');
		if (
			(typeof getStartPoint === 'function' && getStartPoint.call(targetEntity) === pointObject)
			|| (typeof getEndPoint === 'function' && getEndPoint.call(targetEntity) === pointObject)
		) {
			return true;
		}
		const getControlPoints = Reflect.get(targetEntity, 'getControlPoints');
		if (typeof getControlPoints !== 'function') {
			return false;
		}
		const controlPoints = getControlPoints.call(targetEntity) as readonly Object3D[];
		return controlPoints[0] === pointObject
			|| controlPoints[controlPoints.length - 1] === pointObject;
	}

	/**
	 * 判断关联点是否仍被本批次之外的曲线使用。
	 *
	 * @param pointObject 关联点。
	 * @param targetEntitySet 本批待清理实体集合。
	 * @returns 至少存在一条存活连接边时返回 true。
	 */
	private hasSurvivingConnectedEdge(
		pointObject: Object3D,
		targetEntitySet: ReadonlySet<Object3D>,
	): boolean {
		const getConnectedEdgesSnapshot = Reflect.get(pointObject, 'getConnectedEdgesSnapshot');
		if (typeof getConnectedEdgesSnapshot !== 'function') {
			return false;
		}
		const connectedEdges = getConnectedEdgesSnapshot.call(pointObject) as readonly Object3D[];
		return connectedEdges.some(connectedEdge => !targetEntitySet.has(connectedEdge));
	}

	/**
	 * 释放 geometry 资源。
	 * 说明：
	 * 1. 引用实体删除可能一次涉及主实体与多个控制点，必须按批次去重；
	 * 2. 这里只定义引用目标对象需要释放哪些资源，不扩展 WebGPU 释放调度策略；
	 * 3. 若后续要统一资源释放时序，应在独立资源生命周期分支中处理。
	 *
	 * @param cleanupTargets 待释放对象集合。
	 */
	private disposeGeometryResources(cleanupTargets: Object3D[]): void {
		const disposedGeometries = new Set<DisposableGeometry>();
		cleanupTargets.forEach((cleanupTarget) => {
			cleanupTarget.traverse((object) => {
				this.disposeSceneObjectResources(object as DisposableSceneObject, disposedGeometries);
			});
		});
	}

	/**
	 * 释放单个场景对象上的几何资源。
	 * 说明：
	 * 1. SketchPointObject 继承 THREE.Sprite，Sprite 的 geometry 是 Three 内部共享资源；
	 * 2. 引用目标清理只拥有草图实体自身 geometry，不拥有 Sprite 共享 geometry；
	 * 3. 同一批目标可能通过关联点和主实体重复遍历到同一个 geometry，必须按引用去重。
	 *
	 * @param sceneObject 场景对象。
	 * @param disposedGeometries 当前批次已释放的几何集合。
	 */
	private disposeSceneObjectResources(
		sceneObject: DisposableSceneObject,
		disposedGeometries: Set<DisposableGeometry>,
	): void {
        if (!ownsObject3DGeometry(sceneObject)) {
			return;
		}

		const geometry = sceneObject.geometry;
		if (!geometry || disposedGeometries.has(geometry)) {
			return;
		}

		disposedGeometries.add(geometry);
		geometry.dispose();
	}
}
